/**
 * Centralized Query Keys
 *
 * Best Practice: Define all query keys in one place for:
 * - Type safety
 * - Easy refactoring
 * - Consistent invalidation
 * - Auto-completion
 */

export const queryKeys = {
  // Patients
  patients: {
    all: ['patients'] as const,
    lists: () => [...queryKeys.patients.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.patients.lists(), { filters }] as const,
    details: () => [...queryKeys.patients.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.patients.details(), id] as const,
    searches: () => [...queryKeys.patients.all, 'search'] as const,
    search: (query: string) => [...queryKeys.patients.searches(), query] as const,
  },

  // Notes
  notes: {
    all: ['notes'] as const,
    lists: () => [...queryKeys.notes.all, 'list'] as const,
    list: (patientId: string) => [...queryKeys.notes.lists(), patientId] as const,
    details: () => [...queryKeys.notes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notes.details(), id] as const,
    searches: () => [...queryKeys.notes.all, 'search'] as const,
    search: (patientId: string, query: string) =>
      [...queryKeys.notes.searches(), patientId, query] as const,
  },

  // Subscription
  subscription: {
    all: ['subscription'] as const,
    status: () => [...queryKeys.subscription.all, 'status'] as const,
  },

  // User
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    customTemplates: () => [...queryKeys.user.all, 'customTemplates'] as const,
  },

  // Doctors
  doctors: {
    all: ['doctors'] as const,
    lists: () => [...queryKeys.doctors.all, 'list'] as const,
    list: () => [...queryKeys.doctors.lists()] as const,
    details: () => [...queryKeys.doctors.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.doctors.details(), id] as const,
  },

  // Specialties
  specialties: {
    all: ['specialties'] as const,
    list: () => [...queryKeys.specialties.all, 'list'] as const,
    config: (specialty: string) => [...queryKeys.specialties.all, 'config', specialty] as const,
  },

  // Templates
  templates: {
    all: ['templates'] as const,
    list: () => [...queryKeys.templates.all, 'list'] as const,
  },

  // Prompt preview (user-facing)
  prompts: {
    all: ['prompts'] as const,
    preview: (specialty: string, key: string) => [...queryKeys.prompts.all, 'preview', specialty, key] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
  },

  // Chats
  chats: {
    all: ['chats'] as const,
    history: () => [...queryKeys.chats.all, 'history'] as const,
    messages: (sessionId: string) => [...queryKeys.chats.all, 'messages', sessionId] as const,
  },
} as const;
